/**
 * スクリーンショットアップロードサービス
 * Circuit BreakerとRetryメカニズムを使用した信頼性の高いアップロード機能
 */

import { CircuitBreaker, createCircuitBreaker } from '../utils/circuitBreaker';
import { retry, RetryPresets } from '../utils/retryMechanism';
import { CapturedScreenshot } from './screenshotCaptureService';

export interface UploadConfig {
  apiBaseUrl: string;
  maxBatchSize: number;
  timeoutMs: number;
}

export interface UploadResult {
  success: boolean;
  uploadedFiles: Array<{
    fileName: string;
    fileId: string;
    webViewLink: string;
    size: number;
    mimeType: string;
  }>;
  failedFiles: Array<{
    fileName: string;
    error: string;
  }>;
  summary: {
    total: number;
    successful: number;
    failed: number;
    totalSize: number;
  };
}

export class ScreenshotUploadService {
  private uploadCircuitBreaker: CircuitBreaker;
  private config: UploadConfig;

  constructor(config: UploadConfig) {
    this.config = config;

    // アップロード専用Circuit Breaker
    this.uploadCircuitBreaker = createCircuitBreaker({
      failureThreshold: 5, // 5回失敗で遮断
      recoveryTimeout: 120000, // 2分待機
      successThreshold: 3, // 3回成功で復旧
      monitoringPeriod: 60000, // 1分監視期間
    });
  }

  /**
   * スクリーンショット一括アップロード
   */
  async uploadScreenshots(
    screenshots: CapturedScreenshot[],
    metadata: {
      interviewId: string;
      candidateId: string;
      candidateName: string;
      companyId: string;
    }
  ): Promise<UploadResult> {
    if (screenshots.length === 0) {
      return this.createEmptyResult();
    }

    const batches = this.createBatches(screenshots);
    const results = await this.processBatches(batches, metadata);

    return this.mergeResults(results);
  }

  /**
   * 単一バッチのアップロード処理
   */
  private async uploadBatch(
    screenshots: CapturedScreenshot[],
    metadata: any
  ): Promise<UploadResult> {
    return await this.uploadCircuitBreaker.execute(() =>
      retry(async () => {
        const formData = new FormData();

        Object.keys(metadata).forEach((key) => {
          formData.append(key, metadata[key]);
        });

        screenshots.forEach((screenshot, index) => {
          const file = new File([screenshot.blob], screenshot.filename, {
            type: screenshot.blob.type || 'image/jpeg',
            lastModified: screenshot.timestamp.getTime(),
          });

          formData.append('screenshots', file);
        });

        const response = await this.makeUploadRequest(formData);
        const result = await this.parseUploadResponse(response);

        return result;
      }, RetryPresets.UPLOAD)
    );
  }

  /**
   * HTTP アップロードリクエストの実行
   */
  private async makeUploadRequest(formData: FormData): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.config.timeoutMs);

    try {
      const response = await fetch(`${this.config.apiBaseUrl}/screenshots/upload`, {
        method: 'POST',
        body: formData,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Upload failed: ${response.status} ${response.statusText} - ${errorText}`);
      }

      return response;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error(`Upload timeout after ${this.config.timeoutMs}ms`);
      }

      throw error;
    }
  }

  /**
   * アップロードレスポンスの解析
   */
  private async parseUploadResponse(response: Response): Promise<UploadResult> {
    const data = await response.json();

    if (!data.success && response.status >= 400) {
      throw new Error(data.message || 'Upload failed');
    }

    return {
      success: data.success,
      uploadedFiles: data.data?.uploadedFiles || [],
      failedFiles: data.data?.failedFiles || [],
      summary: data.data?.summary || {
        total: 0,
        successful: 0,
        failed: 0,
        totalSize: 0,
      },
    };
  }

  /**
   * スクリーンショットをバッチに分割
   */
  private createBatches(screenshots: CapturedScreenshot[]): CapturedScreenshot[][] {
    const batches: CapturedScreenshot[][] = [];

    for (let i = 0; i < screenshots.length; i += this.config.maxBatchSize) {
      batches.push(screenshots.slice(i, i + this.config.maxBatchSize));
    }

    return batches;
  }

  /**
   * 複数バッチの並列処理（適応的並列度制御）
   */
  private async processBatches(
    batches: CapturedScreenshot[][],
    metadata: any
  ): Promise<UploadResult[]> {
    const results: UploadResult[] = [];
    const MAX_CONCURRENT_BATCHES = 3; // 最大並列数
    const ADAPTIVE_DELAY_BASE = 500; // 基本遅延時間

    // 最初の1バッチだけ先に送り、サーバー側で保存先フォルダを確定させてから残りを並列送信する。
    // 全バッチを同時に送ると、各リクエストが独立に Drive のフォルダを「検索→無ければ作成」して
    // 同名の面接フォルダ／screenshotsフォルダが重複し、スクリーンショットが散らばっていた。
    const chunks: CapturedScreenshot[][][] = [];
    if (batches.length > 0) {
      chunks.push([batches[0]]);
      for (let i = 1; i < batches.length; i += MAX_CONCURRENT_BATCHES) {
        chunks.push(batches.slice(i, i + MAX_CONCURRENT_BATCHES));
      }
    }

    let successfulBatches = 0;
    let failedBatches = 0;

    let processedBatches = 0;
    for (const [chunkIndex, chunk] of chunks.entries()) {
      const chunkOffset = processedBatches;
      processedBatches += chunk.length;
      const chunkPromises = chunk.map(async (batch, batchIndex) => {
        const globalBatchIndex = chunkOffset + batchIndex + 1;
        const batchId = `batch-${globalBatchIndex}`;

        try {
          const result = await this.uploadBatch(batch, metadata);
          return result;
        } catch (error) {
          console.error(`${batchId} upload failed:`, error);

          return {
            success: false,
            uploadedFiles: [],
            failedFiles: batch.map((screenshot) => ({
              fileName: screenshot.filename,
              error: error instanceof Error ? error.message : 'Unknown error',
            })),
            summary: {
              total: batch.length,
              successful: 0,
              failed: batch.length,
              totalSize: batch.reduce((sum, s) => sum + s.blob.size, 0),
            },
          };
        }
      });

      const chunkResults = await Promise.allSettled(chunkPromises);
      for (let index = 0; index < chunkResults.length; index++) {
        const promiseResult = chunkResults[index];

        if (promiseResult.status === 'fulfilled') {
          const result = promiseResult.value;
          results.push(result);

          if (result.success) {
            successfulBatches++;
          } else {
            failedBatches++;
          }
        } else {
          const batch = chunk[index];
          results.push({
            success: false,
            uploadedFiles: [],
            failedFiles: batch.map((screenshot) => ({
              fileName: screenshot.filename,
              error: 'Promise rejection: ' + promiseResult.reason,
            })),
            summary: {
              total: batch.length,
              successful: 0,
              failed: batch.length,
              totalSize: batch.reduce((sum, s) => sum + s.blob.size, 0),
            },
          });
          failedBatches++;
        }
      }

      if (chunkIndex < chunks.length - 1) {
        const failureRate = failedBatches / (successfulBatches + failedBatches);
        const adaptiveDelay = ADAPTIVE_DELAY_BASE * (1 + failureRate * 2);

        await this.sleep(adaptiveDelay);
      }
    }

    return results;
  }

  /**
   * 複数の結果をマージ
   */
  private mergeResults(results: UploadResult[]): UploadResult {
    const merged: UploadResult = {
      success: results.some((r) => r.success),
      uploadedFiles: [],
      failedFiles: [],
      summary: {
        total: 0,
        successful: 0,
        failed: 0,
        totalSize: 0,
      },
    };

    results.forEach((result) => {
      merged.uploadedFiles.push(...result.uploadedFiles);
      merged.failedFiles.push(...result.failedFiles);
      merged.summary.total += result.summary.total;
      merged.summary.successful += result.summary.successful;
      merged.summary.failed += result.summary.failed;
      merged.summary.totalSize += result.summary.totalSize;
    });

    return merged;
  }

  /**
   * 空の結果を作成
   */
  private createEmptyResult(): UploadResult {
    return {
      success: true,
      uploadedFiles: [],
      failedFiles: [],
      summary: {
        total: 0,
        successful: 0,
        failed: 0,
        totalSize: 0,
      },
    };
  }

  /**
   * 待機処理
   */
  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Circuit Breaker統計情報の取得
   */
  getStats() {
    return this.uploadCircuitBreaker.getStats();
  }

  /**
   * Circuit Breakerの手動リセット
   */
  resetCircuitBreaker(): void {
    this.uploadCircuitBreaker.manualReset();
  }

  /**
   * アップロード可能かチェック
   */
  isAvailable(): boolean {
    return this.uploadCircuitBreaker.isAvailable();
  }

  /**
   * API設定を取得
   */
  getApiBaseUrl(): string {
    return this.config.apiBaseUrl;
  }
}
